import { Card, LoadingOverlay, Select, Group, Text, ActionIcon, Tooltip } from "@mantine/core";
import { useEffect, useState, useRef, forwardRef, useImperativeHandle, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import { useSubscription } from '@apollo/client';
import useWebSocket from 'react-use-websocket';
import { IconRefresh } from '@tabler/icons-react';
import { buildApiUrl, buildWsUrl } from '../../../config/endpoints';
import { TOPUP_COMPLETED_SUB } from '../../../queries/OrdersAndWallets';

const CACHE_TTL_MS = 55_000; // 55 s — just under the 60 s write interval

const PLTrend = forwardRef(({ userid }, ref) => {
    const [isLoading, setIsLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [duration, setDuration] = useState('all'); // 'all', '7d', '30d', '90d'
    const [error, setError] = useState(null);
    // Client-side cache: { [duration]: { data: Array, fetchedAt: number } }
    const cache = useRef({});

// TODO: P/L chart has 60s cache — will update via PL_RECALCULATED
// subscription when Go publishes post-topup P/L events
// Tracked in: Real-Time P/L and Chart Updates mentor report
    const getOrderHandler = useCallback(async (owner, selectedDuration = duration, { preferCache = false } = {}) => {
        // Serve from client cache when caller opts in and data is still fresh.
        if (preferCache) {
            const entry = cache.current[selectedDuration];
            if (entry && Date.now() - entry.fetchedAt < CACHE_TTL_MS) {
                setItems(entry.data);
                return;
            }
        }
        setIsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ userid: owner });
            if (selectedDuration && selectedDuration !== 'all') {
                params.append('duration', selectedDuration);
            }
            const response = await fetch(buildApiUrl('/user/pltrend') + '?' + params);
            if (!response.ok) {
                throw new Error(`Error! status: ${response.status}`);
            }
            const result = await response.json();
            cache.current[selectedDuration] = { data: result, fetchedAt: Date.now() };
            setItems(result);
        } catch (err) {
            setError('Failed to load P/L data. Retrying shortly.');
        } finally {
            setIsLoading(false);
        }
    }, [duration]);

    // Expose refresh function to parent components
    useImperativeHandle(ref, () => ({
        refresh: () => getOrderHandler(userid)
    }));

    // Real-time refresh on top-up completion
    useSubscription(TOPUP_COMPLETED_SUB, {
        variables: { userid: String(userid) },
        skip: !userid,
        onData: () => getOrderHandler(userid, duration)
    });

    // Real-time refresh on order fills via WebSocket
    const { lastMessage } = useWebSocket(buildWsUrl('/orders'), {
        shouldReconnect: () => true,
    });
    useEffect(() => {
        if (!lastMessage || !userid) return;
        try {
            const data = JSON.parse(lastMessage.data);
            if (data.type === 'ORDER_FILLED' && String(data.userID) === String(userid)) {
                getOrderHandler(userid, duration);
            }
        } catch (e) { /* ignore parse errors */ }
    }, [lastMessage, userid, duration, getOrderHandler]);

    // Convert data for chart (backend already filters by duration)
    const getFilteredData = () => {
        if (!items || items.length === 0) return [];
        return items.map(item => [Date.parse(item.time), parseFloat(item.amount)]);
    };

    // useEffect(() => {
    //     if (userid) {
    //         getOrderHandler(userid, duration);
    //     }
    // }, [userid, duration, getOrderHandler]);

    // useEffect(() => {
    //     if (userid) {
    //         const intervalId = setInterval(() => getOrderHandler(userid, duration), 60000); // Fetch data every minute
    //         return () => clearInterval(intervalId); // Clean up interval on component unmount
    //     }
    // }, [userid, duration, getOrderHandler]);

    useEffect(() => {
        if (!userid) return;
      
        // initial load (cache first)
        getOrderHandler(userid, duration, { preferCache: true });
      
        // refresh in background every minute
        const intervalId = setInterval(
          () => getOrderHandler(userid, duration, { preferCache: true }),
          60000
        );
      
        return () => clearInterval(intervalId);
      }, [userid, duration, getOrderHandler]);

    const durationOptions = [
        { value: 'all', label: 'All Time' },
        { value: '7d', label: 'Last 7 Days' },
        { value: '30d', label: 'Last 30 Days' },
        { value: '90d', label: 'Last 90 Days' }
    ];

    const hasRealData = items && items.length > 0;

    return (
        <Card withBorder>
            <LoadingOverlay visible={isLoading} overlayBlur={2} />
            <Group position="apart" mb="md">
                <div>
                    <Text weight={500} size="lg">P/L Trend</Text>
                </div>
                <Group spacing="xs">
                    <Select
                        value={duration}
                        onChange={setDuration}
                        data={durationOptions}
                        size="sm"
                        style={{ minWidth: 150 }}
                    />
                    <Tooltip label="Refresh">
                        <ActionIcon
                            variant="subtle"
                            onClick={() => getOrderHandler(userid, duration)}
                            disabled={!userid || isLoading}
                            aria-label="Refresh P/L Trend"
                        >
                            <IconRefresh size={18} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            </Group>
            {error && (
                <Text color="red" size="sm" align="center" mb="sm">{error}</Text>
            )}
            <PLChart pltrend={getFilteredData()} duration={duration} hasRealData={hasRealData} />
        </Card>
    )
});

export default PLTrend;

function PLChart({ pltrend, duration, hasRealData }) {
    // Calculate data statistics for better scaling
    const getDataStats = () => {
        if (!pltrend || pltrend.length === 0) return { 
            min: 0, 
            max: 100000, 
            range: 100000, 
            actualMin: 0, 
            actualMax: 100000 
        };
        
        const values = pltrend.map(item => item[1]);
        const min = Math.min(...values);
        const max = Math.max(...values);
        const range = max - min;
        
        // Handle edge case where all values are the same
        if (range === 0) {
            const baseValue = min;
            const padding = Math.max(baseValue * 0.1, 5000); // 10% or at least $5000
            return {
                min: Math.max(0, baseValue - padding),
                max: baseValue + padding,
                range: 2 * padding,
                actualMin: min,
                actualMax: max
            };
        }
        
        // Dynamic padding based on data range and magnitude
        let paddingPercent = 0.05; // 5% default
        if (range < 10000) paddingPercent = 0.2;      // 20% for small ranges
        else if (range < 50000) paddingPercent = 0.1; // 10% for medium ranges
        
        const padding = Math.max(range * paddingPercent, 1000); // At least $1000 padding
        
        return {
            min: Math.max(0, min - padding), // Don't go below 0
            max: max + padding,
            range: range + (2 * padding),
            actualMin: min,
            actualMax: max
        };
    };

    const stats = getDataStats();
    
    // Determine chart color based on trend and data type
    const getChartColor = () => {
        if (!pltrend || pltrend.length < 2) return hasRealData ? '#228be6' : '#a3a3a3';
        const firstValue = pltrend[0][1];
        const lastValue = pltrend[pltrend.length - 1][1];
        
        if (!hasRealData) {
            // Use muted colors for sample data
            return lastValue >= firstValue ? '#a8d8a8' : '#d8a8a8';
        }
        
        return lastValue >= firstValue ? '#51cf66' : '#ff6b6b';
    };

    const chartColor = getChartColor();

    // Smart Y-axis formatter based on data range
    const formatYAxisValue = (value) => {
        const absValue = Math.abs(value);
        
        if (absValue >= 1000000) {
            return `$${(value / 1000000).toFixed(1)}M`;
        } else if (absValue >= 100000) {
            return `$${(value / 1000).toFixed(0)}K`;
        } else if (absValue >= 10000) {
            return `$${(value / 1000).toFixed(1)}K`;
        } else if (absValue >= 1000) {
            return `$${(value / 1000).toFixed(2)}K`;
        } else if (absValue >= 100) {
            return `$${Math.round(value)}`;
        } else {
            return `$${value.toFixed(2)}`;
        }
    };

    // Smart tooltip formatter
    const formatTooltipValue = (value) => {
        return `$${parseFloat(value).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    const xMin = pltrend?.length ? pltrend[0][0] : undefined;
    const xMax = pltrend?.length ? pltrend[pltrend.length - 1][0] : undefined;

    return (
        <ReactApexChart options={{
            chart: {
                type: 'area',
                stacked: false,
                height: 300,
                zoom: {
                    type: 'x',
                    enabled: true,
                    autoScaleYaxis: true
                },
                toolbar: {
                    autoSelected: 'zoom',
                    show: true,
                    tools: {
                        download: true,
                        selection: true,
                        zoom: true,
                        zoomin: true,
                        zoomout: true,
                        pan: true,
                        reset: true
                    }
                },
                animations: {
                    enabled: true,
                    easing: 'easeinout',
                    speed: 800,
                    animateGradually: {
                        enabled: true,
                        delay: 150
                    },
                    dynamicAnimation: {
                        enabled: true,
                        speed: 350
                    }
                }
            },
            dataLabels: {
                enabled: false
            },
            markers: {
                size: 0,
                hover: {
                    size: 6,
                    sizeOffset: 3
                }
            },
            stroke: {
                curve: 'smooth',
                width: 2
            },
            fill: {
                type: 'gradient',
                gradient: {
                    shadeIntensity: 1,
                    inverseColors: false,
                    opacityFrom: 0.5,
                    opacityTo: 0,
                    stops: [0, 90, 100],
                    colorStops: [{
                        offset: 0,
                        color: chartColor,
                        opacity: 0.5
                    }, {
                        offset: 100,
                        color: chartColor,
                        opacity: 0
                    }]
                },
            },
            colors: [chartColor],
            yaxis: {
                title: {
                    text: 'Total Assets ($)',
                    style: {
                        fontSize: '12px',
                        fontWeight: 500
                    }
                },
                min: stats.min,
                max: stats.max,
                tickAmount: Math.min(8, Math.max(4, Math.floor(stats.range / 10000))),
                forceNiceScale: true,
                labels: {
                    formatter: formatYAxisValue,
                    style: {
                        fontSize: '11px'
                    }
                },
                axisBorder: {
                    show: true,
                    color: '#e0e6ed'
                },
                axisTicks: {
                    show: true,
                    color: '#e0e6ed'
                }
            },
            xaxis: {
                type: 'datetime',
                min: xMin,
                max: xMax,
                labels: {
                    formatter: function(value) {
                        const date = new Date(value);
                        if (duration === '7d') {
                            return date.toLocaleDateString('en-US', { 
                                weekday: 'short', 
                                month: 'short', 
                                day: 'numeric'
                            });
                        } else if (duration === '30d') {
                            return date.toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric' 
                            });
                        } else if (duration === '90d') {
                            return date.toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric' 
                            });
                        }
                        return date.toLocaleDateString('en-US', { 
                            month: 'short', 
                            year: '2-digit' 
                        });
                    },
                    style: {
                        fontSize: '11px',
                        colors: '#666'
                    },
                    rotate: -45,
                    rotateAlways: false,
                    hideOverlappingLabels: true,
                    maxHeight: 120
                },
  tickAmount: Math.min(
    (() => {
      switch (duration) {
        case '7d': return 7;
        case '30d': return 6;
        case '90d': return 5;
        default: return 8;
      }
    })(),
    pltrend?.length || 8
  ),
                axisBorder: {
                    show: true,
                    color: '#e0e6ed'
                },
                axisTicks: {
                    show: true,
                    color: '#e0e6ed'
                },
                title: {
                    text: 'Time Period',
                    style: {
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#666'
                    }
                }
            },
            tooltip: {
                x: {
                    format: 'dd MMM yyyy HH:mm'
                },
                y: {
                    formatter: formatTooltipValue
                },
                theme: 'light',
                style: {
                    fontSize: '12px'
                },
                marker: {
                    show: true
                }
            },
            grid: {
                show: true,
                borderColor: '#e0e6ed',
                strokeDashArray: 3,
                position: 'back',
                xaxis: {
                    lines: {
                        show: false
                    }
                },
                yaxis: {
                    lines: {
                        show: true
                    }
                },
                row: {
                    colors: undefined,
                    opacity: 0.5
                },
                column: {
                    colors: undefined,
                    opacity: 0.5
                },
                padding: {
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0
                }
            },
            noData: {
                text: 'No data available for selected duration',
                align: 'center',
                verticalAlign: 'middle',
                style: {
                    fontSize: '14px',
                    color: '#999'
                }
            },
            annotations: {
                yaxis: pltrend && pltrend.length > 0 ? [
                    {
                        y: stats.actualMin,
                        borderColor: '#ff6b6b',
                        borderWidth: 1,
                        strokeDashArray: 2,
                        opacity: 0.3,
                        label: {
                            text: `Min: ${formatTooltipValue(stats.actualMin)}`,
                            position: 'right',
                            style: {
                                color: '#fff',
                                background: '#ff6b6b',
                                fontSize: '10px'
                            }
                        }
                    },
                    {
                        y: stats.actualMax,
                        borderColor: '#51cf66',
                        borderWidth: 1,
                        strokeDashArray: 2,
                        opacity: 0.3,
                        label: {
                            text: `Max: ${formatTooltipValue(stats.actualMax)}`,
                            position: 'right',
                            style: {
                                color: '#fff',
                                background: '#51cf66',
                                fontSize: '10px'
                            }
                        }
                    }
                ] : []
            }
        }} series={[{
            name: hasRealData ? "Total Assets" : "Sample P/L Trend",
            data: pltrend,
        }]} type="area" height={314} />
    )
}